// src/components/TherapistClientList.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Select,
  IconButton,
  useToast,
  useDisclosure,
  Text,
  Badge,
  Heading,
  Card,
  CardBody,
  Divider,
  Flex,
  Spacer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Stat,
  StatLabel, 
  StatNumber,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormControl,
  FormLabel,
  Progress,
  CloseButton,
  Tooltip,
} from '@chakra-ui/react';
import { ViewIcon, AttachmentIcon, ChevronLeftIcon, InfoIcon } from '@chakra-ui/icons';
import supabase from '../supabaseClient';
import { getClientDisplayAge } from '../utils/clientAge';
import { formatNameLastCommaFirst, compareNamesByFamilyName } from '../utils/clientNameDisplay';
import {
  formatAttendanceStatusLabel,
  getAttendanceStatusColorScheme,
  isCancelledOrLateCancellationStatus,
  formatMakeupBadgeDate,
} from '../utils/attendanceDisplay';

// Add this component after your imports in TherapistClientList.js
const DocumentUploadSection = ({ clientId, isNewClient = false, therapistMode = false }) => {
  const [documents, setDocuments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const toast = useToast();

  useEffect(() => {
    if (clientId && !isNewClient) {
      fetchClientDocuments();
    }
  }, [clientId, isNewClient]);

  const fetchClientDocuments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event, category, type) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newUploads = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      category,
      type,
      title: file.name.split('.')[0],
      description: '',
      status: 'pending',
      progress: 0,
      error: null
    }));

    setUploadQueue(prev => [...prev, ...newUploads]);
  };

  const updateUploadItem = (id, updates) => {
    setUploadQueue(prev => 
      prev.map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  const removeUploadItem = (id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const uploadFile = async (uploadItem) => {
    try {
      updateUploadItem(uploadItem.id, { status: 'uploading', progress: 0 });

      const fileExt = uploadItem.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${clientId}/${uploadItem.category.replace(' ', '_').toLowerCase()}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('client-documents')
        .upload(filePath, uploadItem.file, {
          onUploadProgress: (progress) => {
            const percentage = (progress.loaded / progress.total) * 100;
            updateUploadItem(uploadItem.id, { progress: percentage });
          }
        });

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('client_documents')
        .insert({
          client_id: clientId,
          document_category: uploadItem.category,
          document_type: uploadItem.type,
          title: uploadItem.title || uploadItem.file.name.split('.')[0],
          description: uploadItem.description,
          file_name: uploadItem.file.name,
          file_path: filePath,
          file_size: uploadItem.file.size,
          mime_type: uploadItem.file.type
        });

      if (dbError) throw dbError;

      updateUploadItem(uploadItem.id, { 
        status: 'completed', 
        progress: 100 
      });

      if (!isNewClient) {
        fetchClientDocuments();
      }

      toast({
        title: 'Document uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      updateUploadItem(uploadItem.id, { 
        status: 'error', 
        error: error.message 
      });
      
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const uploadAllPending = async () => {
    const pendingUploads = uploadQueue.filter(item => item.status === 'pending');
    
    for (const upload of pendingUploads) {
      await uploadFile(upload);
    }
  };

  // Modified document categories for therapist mode - using database-allowed categories
  const documentCategories = therapistMode ? [
    {
      category: 'Evaluations',
      types: ['Initial Evaluation', 'Progress Report', 'Re-evaluation', 'Other'],
      description: 'Assessment and evaluation reports'
    },
    {
      category: 'Session Notes',
      types: ['Session Notes', 'Therapy Notes', 'Progress Notes', 'Other'],
      description: 'Session documentation and progress notes'
    },
    {
      category: 'Progress Reports',
      types: ['Progress Report', 'Quarterly Report', 'Summary Report', 'Other'],
      description: 'Progress reports and summaries'
    },
    {
      category: 'Medical Records',
      types: ['Therapy Report', 'Medical Document', 'Other'],
      description: 'Medical and therapy documentation'
    }
  ] : [
    {
      category: 'Initial Documents',
      types: ['DevPed Report', 'Doctor Referral', 'Previous Progress Report', 'Other'],
      description: 'Documents provided during initial consultation'
    },
    {
      category: 'Evaluations', 
      types: ['Initial Evaluation', 'Progress Report', 'Other'],
      description: 'Assessment and evaluation reports'
    },
    {
      category: 'Medical Records',
      types: ['Medical Document', 'Insurance Document', 'Other'],
      description: 'Medical history and insurance documents'
    },
    {
      category: 'Administrative',
      types: ['Insurance Document', 'Other'],
      description: 'Administrative and billing documents'
    }
  ];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'uploading': return 'blue';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60);

      if (error) throw error;

      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: `Downloading ${doc.file_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isNewClient) {
    return (
      <Alert status="info">
        <AlertIcon />
        Document upload will be available after saving the client.
      </Alert>
    );
  }

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <Card>
            <CardBody>
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Upload Queue</Heading>
                <Button 
                  size="sm" 
                  colorScheme="blue"
                  onClick={uploadAllPending}
                  isDisabled={!uploadQueue.some(item => item.status === 'pending')}
                >
                  Upload All
                </Button>
              </HStack>
              <VStack spacing={2} align="stretch">
                {uploadQueue.map(item => (
                  <Box key={item.id} p={3} borderWidth="1px" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium">{item.file.name}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {item.category} - {item.type} ({formatFileSize(item.file.size)})
                        </Text>
                      </VStack>
                      <HStack>
                        <Badge colorScheme={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                        <CloseButton 
                          size="sm" 
                          onClick={() => removeUploadItem(item.id)}
                          isDisabled={item.status === 'uploading'}
                        />
                      </HStack>
                    </HStack>
                    
                    {item.status === 'uploading' && (
                      <Progress value={item.progress} size="sm" colorScheme="blue" />
                    )}
                    
                    {item.status === 'error' && (
                      <Text fontSize="xs" color="red.500">{item.error}</Text>
                    )}
                    
                    {item.status === 'pending' && (
                      <VStack spacing={2} align="stretch">
                        <Input
                          placeholder="Document title"
                          value={item.title}
                          onChange={(e) => updateUploadItem(item.id, { title: e.target.value })}
                          size="sm"
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={item.description}
                          onChange={(e) => updateUploadItem(item.id, { description: e.target.value })}
                          size="sm"
                        />
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Upload Sections */}
        <Accordion allowMultiple>
          {documentCategories.map((categoryInfo, index) => (
            <AccordionItem key={index}>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">{categoryInfo.category}</Text>
                  <Text fontSize="sm" color="gray.600">{categoryInfo.description}</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={3} align="stretch">
                  {categoryInfo.types.map(type => (
                    <Box key={type}>
                      <FormLabel fontSize="sm">{type}</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        multiple
                        onChange={(e) => handleFileSelect(e, categoryInfo.category, type)}
                        size="sm"
                      />
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Existing Documents */}
        {documents.length > 0 && (
          <Card>
            <CardBody>
              <Heading size="sm" mb={3}>Uploaded Documents</Heading>
              <VStack spacing={2} align="stretch">
                {documents.map(doc => (
                  <HStack key={doc.id} p={2} borderWidth="1px" borderRadius="md">
                    <AttachmentIcon color="blue.500" />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{doc.title}</Text>
                      <Text fontSize="xs" color="gray.600">
                        {doc.document_category} - {doc.document_type}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </Text>
                    </VStack>
                    <Button 
                      size="xs" 
                      variant="outline"
                      onClick={() => downloadDocument(doc)}
                    >
                      Download
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

// Add this component after imports and before ClientDetailModal
const ClientAttendanceLog = ({ client, therapistId, onBack }) => {
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null); // Add status filter state
  const toast = useToast();

  useEffect(() => {
    fetchAttendanceLogs();
  }, [client, therapistId]);

  const formatTime = (time) => {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  const fetchAttendanceLogs = async () => {
    try {
      // First, get the attendance logs
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select(`
          *,
          session:sessions (
            day,
            time,
            is_makeup,
            makeup_for,
            therapist:therapists (
              name,
              specialization
            )
          )
        `)
        .eq('client_id', client.id)
        .eq('therapist_id', therapistId) // Only show sessions with this therapist
        .order('attendance_date', { ascending: false });

      if (attendanceError) throw attendanceError;

      // Then, get makeup sessions linked to cancelled/late-cancelled rows (completed makeup or scheduled slot)
      const linkedMakeupAttendanceIds = attendanceData
        .filter(
          (log) =>
            isCancelledOrLateCancellationStatus(log.status) &&
            (log.madeup_for === true || log.makeup_scheduled === true)
        )
        .map((log) => log.id);

      let makeupSessionsData = [];
      if (linkedMakeupAttendanceIds.length > 0) {
        console.log('Looking for makeup sessions for attendance IDs:', linkedMakeupAttendanceIds);

        const { data: makeupData, error: makeupError } = await supabase
          .from('sessions')
          .select('session_attendance_id, valid_from, id')
          .in('session_attendance_id', linkedMakeupAttendanceIds);

        if (makeupError) throw makeupError;
        makeupSessionsData = makeupData || [];
        console.log('Found makeup sessions:', makeupSessionsData);
      }

      // Combine the data
      const logsWithMakeupDates = attendanceData.map(log => {
        const makeupSession = makeupSessionsData.find(
          makeup => String(makeup.session_attendance_id) === String(log.id)
        );
        
        if (
          log.madeup_for === true &&
          isCancelledOrLateCancellationStatus(log.status)
        ) {
          console.log(
            `For cancelled or late-cancelled session ${log.id} (${log.attendance_date}):`, {
            foundMakeupSession: !!makeupSession,
            makeupDate: makeupSession?.valid_from,
            originalDate: log.attendance_date
          });
        }
        
        return {
          ...log,
          makeup_session_date: makeupSession?.valid_from
        };
      });

      setAttendanceLogs(logsWithMakeupDates);
    } catch (error) {
      toast({
        title: 'Error fetching attendance logs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStats = () => {
    const total = attendanceLogs.length;
    const present = attendanceLogs.filter(log => log.status === 'present').length;
    const cancelled = attendanceLogs.filter(log => log.status === 'cancelled').length;
    
    // Count sessions that require makeup (madeup_for is not null - either true or false)
    const requiredForMakeup = attendanceLogs.filter(log => log.madeup_for !== null).length;
    
    // Count sessions that have been made up (madeup_for is true)
    const madeUp = attendanceLogs.filter(log => log.madeup_for === true).length;
    
    // Count makeup sessions (sessions created to make up for others)
    const makeupSessions = attendanceLogs.filter(log => log.session?.is_makeup === true).length;
    
    // Actual sessions = total minus makeup sessions
    const actualSessions = total - makeupSessions;
    
    return { total, present, cancelled, requiredForMakeup, madeUp, actualSessions };
  };

  const stats = getStats();

  // Filter logs based on selected status
  const getFilteredLogs = () => {
    if (!statusFilter) return attendanceLogs;
    
    switch (statusFilter) {
      case 'present':
        return attendanceLogs.filter(log => log.status === 'present');
      case 'cancelled':
        return attendanceLogs.filter(log => log.status === 'cancelled');
      case 'makeup_required':
        return attendanceLogs.filter(log => log.madeup_for === false);
      case 'made_up':
        return attendanceLogs.filter(log => log.madeup_for === true);
      case 'actual_sessions':
        return attendanceLogs.filter(log => !log.session?.is_makeup);
      default:
        return attendanceLogs;
    }
  };

  const filteredLogs = getFilteredLogs();

  const handleStatClick = (filterType) => {
    if (statusFilter === filterType) {
      setStatusFilter(null); // Clear filter if same stat is clicked
    } else {
      setStatusFilter(filterType);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <Box>
      <Button leftIcon={<ChevronLeftIcon />} onClick={onBack} mb={6}>
        Back to Clients
      </Button>
      
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg">{formatNameLastCommaFirst(client.name)}'s Session Log</Heading>
          {client.nickname && (
            <Text fontSize="lg" color="gray.600">"{client.nickname}"</Text>
          )}
        </Box>

        <HStack spacing={8} p={4} bg="gray.50" borderRadius="md">
          <Box 
            as="button"
            onClick={() => handleStatClick('actual_sessions')}
            p={2}
            borderRadius="md"
            bg={statusFilter === 'actual_sessions' ? 'blue.100' : 'transparent'}
            borderWidth={statusFilter === 'actual_sessions' ? '2px' : '0px'}
            borderColor="blue.300"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s"
          >
            <Stat>
              <HStack spacing={1}>
                <StatLabel>Total Sessions</StatLabel>
                <Tooltip 
                  label="Total number of sessions including makeup sessions. Actual sessions are original scheduled sessions only. Click to filter."
                  fontSize="sm"
                >
                  <InfoIcon boxSize={3} color="gray.400" />
                </Tooltip>
              </HStack>
              <StatNumber>
                <HStack>
                  <Text fontSize="2xl">{stats.total}</Text>
                  <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                    ({stats.actualSessions} actual sessions)
                  </Text>
                </HStack>
              </StatNumber>
            </Stat>
          </Box>
          <Box 
            as="button"
            onClick={() => handleStatClick('present')}
            p={2}
            borderRadius="md"
            bg={statusFilter === 'present' ? 'green.100' : 'transparent'}
            borderWidth={statusFilter === 'present' ? '2px' : '0px'}
            borderColor="green.300"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s"
          >
            <Stat>
              <StatLabel>Present</StatLabel>
              <StatNumber color="green.500">{stats.present}</StatNumber>
            </Stat>
          </Box>
          <Box 
            as="button"
            onClick={() => handleStatClick('cancelled')}
            p={2}
            borderRadius="md"
            bg={statusFilter === 'cancelled' ? 'orange.100' : 'transparent'}
            borderWidth={statusFilter === 'cancelled' ? '2px' : '0px'}
            borderColor="orange.300"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s"
          >
            <Stat>
              <HStack spacing={1}>
                <StatLabel>Cancelled</StatLabel>
                <Tooltip 
                  label="Number in parentheses shows how many cancelled sessions require makeup sessions to be scheduled. Click to filter."
                  fontSize="sm"
                >
                  <InfoIcon boxSize={3} color="gray.400" />
                </Tooltip>
              </HStack>
              <StatNumber color="orange.500">
                <HStack>
                  <Text fontSize="2xl" color="orange.500">{stats.cancelled}</Text>
                  <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                    ({stats.requiredForMakeup} required for makeup)
                  </Text>
                </HStack>
              </StatNumber>
            </Stat>
          </Box>
          <Box 
            as="button"
            onClick={() => handleStatClick('made_up')}
            p={2}
            borderRadius="md"
            bg={statusFilter === 'made_up' ? 'purple.100' : 'transparent'}
            borderWidth={statusFilter === 'made_up' ? '2px' : '0px'}
            borderColor="purple.300"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s"
          >
            <Stat>
              <HStack spacing={1}>
                <StatLabel>Made up for</StatLabel>
                <Tooltip 
                  label="Shows how many required makeup sessions have been successfully completed. Click to filter."
                  fontSize="sm"
                >
                  <InfoIcon boxSize={3} color="gray.400" />
                </Tooltip>
              </HStack>
              <StatNumber color="purple.500">
                {stats.madeUp} out of {stats.requiredForMakeup}
              </StatNumber>
            </Stat>
          </Box>
          <Box 
            as="button"
            onClick={() => handleStatClick('makeup_required')}
            p={2}
            borderRadius="md"
            bg={statusFilter === 'makeup_required' ? 'red.100' : 'transparent'}
            borderWidth={statusFilter === 'makeup_required' ? '2px' : '0px'}
            borderColor="red.300"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s"
          >
            <Stat>
              <HStack spacing={1}>
                <StatLabel>Makeup Required</StatLabel>
                <Tooltip 
                  label="Sessions that were cancelled and still need to be made up. Click to filter."
                  fontSize="sm"
                >
                  <InfoIcon boxSize={3} color="gray.400" />
                </Tooltip>
              </HStack>
              <StatNumber color="red.500">
                {stats.requiredForMakeup - stats.madeUp}
              </StatNumber>
            </Stat>
          </Box>
        </HStack>

        {/* Filter indicator and clear button */}
        {statusFilter && (
          <HStack justify="space-between" p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
            <Text fontSize="sm" color="blue.700">
              Showing filtered results: <strong>
                {statusFilter === 'present' && 'Present sessions'}
                {statusFilter === 'cancelled' && 'Cancelled sessions'}
                {statusFilter === 'makeup_required' && 'Sessions requiring makeup'}
                {statusFilter === 'made_up' && 'Sessions that have been made up'}
                {statusFilter === 'actual_sessions' && 'Actual sessions (excluding makeup)'}
              </strong> ({filteredLogs.length} of {attendanceLogs.length} sessions)
            </Text>
            <Button size="sm" variant="outline" colorScheme="blue" onClick={() => setStatusFilter(null)}>
              Clear Filter
            </Button>
          </HStack>
        )}

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Date/Time</Th>
              <Th>Session Type</Th>
              <Th>Service Type</Th>
              <Th>Status</Th>
              <Th>Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredLogs.map(log => (
              <Tr key={log.id}>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text>{new Date(log.attendance_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {log.session.day}, {formatTime(log.session.time)}
                    </Text>
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme={log.session_type === 'Regular' ? 'blue' : 'green'}>
                    {log.session_type}
                  </Badge>
                </Td>
                <Td>{log.session.therapist.specialization}</Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme={getAttendanceStatusColorScheme(log.status)}>
                      {formatAttendanceStatusLabel(log.status)}
                    </Badge>
                    {log.session?.is_makeup && log.session?.makeup_for && (
                      <Badge colorScheme="cyan" variant="outline" size="sm">
                        {log.status === 'present'
                          ? `Makeup for ${formatMakeupBadgeDate(log.session.makeup_for)}`
                          : `Supposed Makeup for ${formatMakeupBadgeDate(log.session.makeup_for)}`}
                      </Badge>
                    )}
                    {log.voided_linked_makeup_on &&
                      isCancelledOrLateCancellationStatus(log.status) && (
                        <>
                          <Badge colorScheme="purple" variant="outline" size="sm">
                            Supposed Makeup on{' '}
                            {formatMakeupBadgeDate(log.voided_linked_makeup_on)} cancelled by therapist
                          </Badge>
                          <Badge colorScheme="green" variant="outline" size="sm">
                            Makeup no longer required
                          </Badge>
                        </>
                      )}
                    {log.makeup_fee_voided_by_therapist &&
                      log.session?.is_makeup &&
                      isCancelledOrLateCancellationStatus(log.status) &&
                      log.session?.makeup_for && (
                        <Badge colorScheme="purple" variant="outline" size="sm">
                          Makeup for {formatMakeupBadgeDate(log.session.makeup_for)} cancelled by therapist
                        </Badge>
                      )}
                    {isCancelledOrLateCancellationStatus(log.status) &&
                      log.makeup_scheduled === true &&
                      !log.voided_linked_makeup_on &&
                      log.madeup_for !== true &&
                      log.makeup_session_date && (
                        <Badge colorScheme="blue" variant="outline" size="sm">
                          Makeup scheduled on{' '}
                          {new Date(log.makeup_session_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Badge>
                      )}
                    {log.paid_noshow && (
                      <Badge colorScheme="orange" variant="solid" size="sm">
                        Paid No Show Fee
                      </Badge>
                    )}
                    {log.madeup_for === false && log.makeup_scheduled === false && !log.paid_noshow && (
                      <Badge colorScheme="red" variant="outline" size="sm">
                        Makeup Required
                      </Badge>
                    )}
                    {log.madeup_for === true &&
                      isCancelledOrLateCancellationStatus(log.status) &&
                      log.makeup_session_date && (
                      <Badge colorScheme="green" variant="outline" size="sm">
                        Made up for on {new Date(log.makeup_session_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Badge>
                    )}
                  </VStack>
                </Td>
                <Td>{log.notes || '-'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </VStack>
    </Box>
  );
};

// Updated ClientDetailModal for TherapistClientList.js
const ClientDetailModal = ({ isOpen, onClose, client }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [documentsPerPage] = useState(10);
  
  // Tab-specific pagination
  const [tabPagination, setTabPagination] = useState({
    initial: { currentPage: 1, total: 0 },
    recent: { currentPage: 1, total: 0 },
    evaluations: { currentPage: 1, total: 0 },
    all: { currentPage: 1, total: 0 }
  });
  
  const toast = useToast();

  useEffect(() => {
    if (client && isOpen) {
      fetchCategoryCounts();
      // Then load the Initial Documents tab
      handleTabChange(0);
    }
  }, [client, isOpen]);

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeDocTab]);

  const fetchClientDocuments = async (page = 1, category = null) => {
    try {
      setIsLoadingDocs(true);
      
      // First get the count to validate pagination
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      if (category) {
        countQuery = countQuery.eq('document_category', category);
      }

      const { count, error: countError } = await countQuery;
      
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // If no documents, return early
      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        await fetchCategoryCounts();
        return;
      }

      // Validate page number
      const maxPage = Math.ceil(totalCount / documentsPerPage);
      const validPage = Math.min(Math.max(1, page), maxPage);
      
      // Calculate pagination range
      const from = (validPage - 1) * documentsPerPage;
      const to = Math.min(from + documentsPerPage - 1, totalCount - 1);

      // Only apply range if we have documents
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      if (category) {
        dataQuery = dataQuery.eq('document_category', category);
      }

      dataQuery = dataQuery.range(from, to);

      const { data, error } = await dataQuery;
  
      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(validPage);

      // Fetch counts for badges (only once)
      if (page === 1) {
        await fetchCategoryCounts();
      }
      
    } catch (error) {
      console.error('Fetch error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      // Get counts for each category
      const categories = ['Initial Documents', 'Session Notes', ['Evaluations', 'Progress Reports']];
      const counts = {};

      // Initial Documents count
      const { count: initialCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .eq('document_category', 'Initial Documents');

      // Recent Session Notes count
      const { count: notesCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .eq('document_category', 'Session Notes');

      // Evaluations and Progress Reports count
      const { count: evaluationsCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .in('document_category', ['Evaluations', 'Progress Reports']);

      // Total count
      const { count: totalCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      setTabPagination({
        initial: { currentPage: 1, total: initialCount || 0 },
        recent: { currentPage: 1, total: notesCount || 0 },
        evaluations: { currentPage: 1, total: evaluationsCount || 0 },
        all: { currentPage: 1, total: totalCount || 0 }
      });

    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  };

  const handleTabChange = async (tabIndex) => {
    setActiveDocTab(tabIndex);
    
    // Don't fetch documents for upload tab
    if (tabIndex === 3) {
      return;
    }
    
    setIsLoadingDocs(true);
    
    try {
      // Get count first
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      let category = null;
      switch (tabIndex) {
        case 0: // Initial Documents
          countQuery = countQuery.eq('document_category', 'Initial Documents');
          category = 'Initial Documents';
          break;
        case 1: // Recent Notes
          countQuery = countQuery.eq('document_category', 'Session Notes');
          category = 'Session Notes';
          break;
        case 2: // Evaluations
          countQuery = countQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no filter
          break;
        default:
          break;
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // If no documents, return early
      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        setIsLoadingDocs(false);
        return;
      }

      // Fetch first page of documents
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      switch (tabIndex) {
        case 0: // Initial Documents
          dataQuery = dataQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          dataQuery = dataQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          dataQuery = dataQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      // Apply pagination - calculate safe range
      const from = 0; // Always start from first page
      const to = Math.min(documentsPerPage - 1, totalCount - 1);
      
      dataQuery = dataQuery.range(from, to);
      const { data, error } = await dataQuery;

      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(1);

    } catch (error) {
      console.error('Tab change error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handlePageChange = async (newPage) => {
    setIsLoadingDocs(true);
    
    try {
      // Get current count first to validate page
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      // Apply same filters as current tab
      switch (activeDocTab) {
        case 0: // Initial Documents
          countQuery = countQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          countQuery = countQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          countQuery = countQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // Validate page
      const maxPage = Math.ceil(totalCount / documentsPerPage);
      const validPage = Math.min(Math.max(1, newPage), maxPage);

      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        setIsLoadingDocs(false);
        return;
      }

      // Fetch documents for the validated page
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      // Apply same filters as current tab
      switch (activeDocTab) {
        case 0: // Initial Documents
          dataQuery = dataQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          dataQuery = dataQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          dataQuery = dataQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      const from = (validPage - 1) * documentsPerPage;
      const to = Math.min(from + documentsPerPage - 1, totalCount - 1);
      
      dataQuery = dataQuery.range(from, to);
      const { data, error } = await dataQuery;

      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(validPage);

    } catch (error) {
      console.error('Page change error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60);

      if (error) throw error;

      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: `Downloading ${doc.file_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const DocumentList = ({ docs, showCategory = false }) => {
    if (isLoadingDocs) {
      return (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="lg" />
            <Text color="gray.500">Loading documents...</Text>
          </VStack>
        </Center>
      );
    }

    if (docs.length === 0) {
      return (
        <Text color="gray.500" fontStyle="italic" textAlign="center" py={4}>
          No documents found
        </Text>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        <VStack spacing={2} align="stretch">
          {docs.map(doc => (
            <HStack key={doc.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
              <AttachmentIcon color="blue.500" />
              <VStack align="start" spacing={0} flex={1}>
                <Text fontSize="sm" fontWeight="medium">{doc.title}</Text>
                {showCategory && (
                  <Text fontSize="xs" color="blue.600">
                    {doc.document_category} - {doc.document_type}
                  </Text>
                )}
                <Text fontSize="xs" color="gray.600">
                  {doc.document_type} • {formatFileSize(doc.file_size)}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Text>
                {doc.description && (
                  <Text fontSize="xs" color="gray.600" fontStyle="italic">
                    {doc.description}
                  </Text>
                )}
              </VStack>
              <Button 
                size="xs" 
                variant="outline"
                colorScheme="blue"
                onClick={() => downloadDocument(doc)}
              >
                Download
              </Button>
            </HStack>
          ))}
        </VStack>

        {/* Pagination */}
        {totalDocuments > documentsPerPage && (
          <PaginationControls />
        )}
      </VStack>
    );
  };

  const PaginationControls = () => {
    const totalPages = Math.ceil(totalDocuments / documentsPerPage);
    const startDoc = (currentPage - 1) * documentsPerPage + 1;
    const endDoc = Math.min(currentPage * documentsPerPage, totalDocuments);

    return (
      <VStack spacing={3}>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Showing {startDoc}-{endDoc} of {totalDocuments} documents
        </Text>
        
        <HStack spacing={2} justify="center">
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage === 1 || isLoadingDocs}
            variant="outline"
          >
            Previous
          </Button>
          
          {/* Page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                size="sm"
                onClick={() => handlePageChange(pageNum)}
                isDisabled={isLoadingDocs}
                colorScheme={currentPage === pageNum ? "blue" : "gray"}
                variant={currentPage === pageNum ? "solid" : "outline"}
              >
                {pageNum}
              </Button>
            );
          })}
          
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={currentPage === totalPages || isLoadingDocs}
            variant="outline"
          >
            Next
          </Button>
        </HStack>
      </VStack>
    );
  };

  if (!client) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>{formatNameLastCommaFirst(client.name)}</Text>
            {client.nickname && (
              <Text fontSize="md" color="gray.600" fontWeight="normal">
                "{client.nickname}"
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Basic Information */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Basic Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Age:</Text>
                    <Text>{getClientDisplayAge(client) ?? 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Gender:</Text>
                    <Text>{client.gender || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Diagnosis:</Text>
                    <Text>{client.diagnosis || 'Not specified'}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Guardian Information */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Guardian Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Name:</Text>
                    <Text>{client.guardian_name || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Contact:</Text>
                    <Text>{client.guardian_contact || 'Not specified'}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Services */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Services & Schedule</Heading>
                <VStack spacing={3} align="stretch">
                  {client.services && client.services.length > 0 ? (
                    client.services.map((service, index) => (
                      <Box key={index} p={3} borderWidth="1px" borderRadius="md">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">
                            {service.service_type} - {service.frequency}
                          </Text>
                          {service.therapists && service.therapists.length > 0 ? (
                            <Text fontSize="sm" color="gray.600">
                              Therapist/s: {service.therapists.join(', ')}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                              No therapists assigned
                            </Text>
                          )}
                          <Text fontSize="sm" color="gray.500">
                            Schedule: {service.schedule || 'Not specified'}
                          </Text>
                        </VStack>
                      </Box>
                    ))
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No services assigned</Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Notes */}
            {client.notes && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={3}>Notes</Heading>
                  <Text>{client.notes}</Text>
                </CardBody>
              </Card>
            )}

            {/* Document Management with Tabs and Pagination */}
            <Card>
              <CardBody>
                <HStack justify="space-between" mb={3}>
                  <Heading size="sm">Documents</Heading>
                  <Badge colorScheme="blue">{tabPagination.all.total} total</Badge>
                </HStack>
                
                <Tabs index={activeDocTab} onChange={handleTabChange}>
                  <TabList>
                    <Tab>
                      Initial Documents
                      {tabPagination.initial.total > 0 && (
                        <Badge ml={2} colorScheme="red" variant="solid">
                          {tabPagination.initial.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Recent Notes
                      {tabPagination.recent.total > 0 && (
                        <Badge ml={2} colorScheme="green" variant="solid">
                          {tabPagination.recent.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Evaluations
                      {tabPagination.evaluations.total > 0 && (
                        <Badge ml={2} colorScheme="purple" variant="solid">
                          {tabPagination.evaluations.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Upload Documents
                      <Badge ml={2} colorScheme="blue" variant="solid">
                        +
                      </Badge>
                    </Tab>
                    <Tab>
                      All Documents
                      <Badge ml={2} colorScheme="blue" variant="solid">
                        {tabPagination.all.total}
                      </Badge>
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Initial Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            These are the documents submitted during initial consultation.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Recent Session Notes Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Session notes ordered by most recent.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Evaluations Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Evaluation reports and progress assessments.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Upload Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Upload therapy-related documents such as evaluations, progress reports, and session notes.
                          </Text>
                        </Alert>
                        <DocumentUploadSection 
                          clientId={client.id} 
                          therapistMode={true}
                          onDocumentUploaded={fetchClientDocuments}
                        />
                      </VStack>
                    </TabPanel>

                    {/* All Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            All documents for this client, grouped by category.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} showCategory={true} />
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const TherapistClientList = ({ therapistId, LoadingComponent }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [uniqueServices, setUniqueServices] = useState([]);
  const [selectedClientForLog, setSelectedClientForLog] = useState(null); // Add this line
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchTherapistClients();
  }, [therapistId]);

  // Update filtered clients whenever search or filters change
  useEffect(() => {
    let result = clients;

    // Name search filter
    if (searchTerm) {
      result = result.filter(client => 
        client.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Service filter
    if (serviceFilter) {
      result = result.filter(client => 
        client.services.some(service => 
          service.service_type === serviceFilter
        )
      );
    }

    setFilteredClients(result);
  }, [clients, searchTerm, serviceFilter]);

  const fetchTherapistClients = async () => {
    try {
      // Get current date for filtering valid sessions
      const currentDate = new Date().toISOString().split('T')[0];

      // Get all active sessions for this therapist with client and service data
      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select(`
          *,
          client:clients (
            *,
            client_services (
              *
            )
          )
        `)
        .eq('therapist_id', therapistId)
        .or(`valid_until.is.null,valid_until.gte.${currentDate}`);

      if (sessionsError) throw sessionsError;

      // Get therapist info for display
      const { data: therapistData, error: therapistError } = await supabase
        .from('therapists')
        .select('name, specialization')
        .eq('id', therapistId)
        .single();

      if (therapistError) throw therapistError;

      // Map specialization to service type
      const specializationToServiceType = {
        'Occupational Therapy': 'OT',
        'Speech Therapy': 'ST', 
        'Physical Therapy': 'PT',
        'Play Group': 'PG'
      };

      const therapistServiceType = specializationToServiceType[therapistData.specialization];

      // Group sessions by client and process their data
      const clientsMap = new Map();
      
      sessions.forEach(session => {
        const client = session.client;
        
        if (!clientsMap.has(client.id)) {
          // Filter valid services for current date
          const validServices = Array.isArray(client.client_services) 
            ? client.client_services.filter(service => 
                (!service.pricing_valid_from || service.pricing_valid_from <= currentDate) &&
                (!service.pricing_valid_until || service.pricing_valid_until >= currentDate)
              )
            : [];

          clientsMap.set(client.id, {
            ...client,
            sessions: [],
            services: validServices
          });
        }

        // Add session to client's sessions array
        clientsMap.get(client.id).sessions.push({
          day: session.day,
          time: session.time,
          session_type: session.session_type
        });
      });

      // Process clients to format services with schedule information
      const processedClients = Array.from(clientsMap.values()).map(client => {
        // Get unique schedule entries for this client with this therapist
        const scheduleEntries = client.sessions
          .map(session => `${session.day} ${formatTime(session.time)}`)
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
          .sort(); // Sort chronologically

        // Process services to include schedule and therapist info
        const processedServices = client.services.map(service => {
          // Only show schedule for services that match this therapist's specialization
          const isTherapistService = service.service_type === therapistServiceType;
          
          return {
            ...service,
            therapists: isTherapistService ? [therapistData.name] : ['Other Therapist'],
            schedule: isTherapistService && scheduleEntries.length > 0 
              ? scheduleEntries.join(' | ')
              : (isTherapistService ? 'Not scheduled' : 'Other therapist\'s schedule')
          };
        });

        return {
          ...client,
          services: processedServices
        };
      }).sort(compareNamesByFamilyName);

      setClients(processedClients);

      // Extract unique services for filter dropdown
      const allServices = new Set();
      processedClients.forEach(client => {
        client.services.forEach(service => {
          allServices.add(service.service_type);
        });
      });
      setUniqueServices(Array.from(allServices));

    } catch (error) {
      console.error('Error fetching therapist clients:', error);
      toast({
        title: 'Error fetching clients',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const formatTime = (time) => {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    onOpen();
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <Box>
      {selectedClientForLog ? (
        <ClientAttendanceLog 
          client={selectedClientForLog}
          therapistId={therapistId}
          onBack={() => setSelectedClientForLog(null)}
        />
      ) : (
        <VStack spacing={4} align="stretch">
          <Flex align="center">
            <Heading size="lg">My Clients</Heading>
            <Spacer />
            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
              {filteredClients.length} clients
            </Badge>
          </Flex>
  
          {/* Search and Filter */}
          <HStack spacing={4}>
            <Input 
              placeholder="Search by client name" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select 
              placeholder="Filter by Service"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              maxW="200px"
            >
              {uniqueServices.map(service => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </Select>
          </HStack>
  
          {/* Clients Table */}
          {filteredClients.length > 0 ? (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Client Name</Th>
                  <Th>Age/Gender</Th>
                  <Th>Services & Schedule</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredClients.map(client => (
                  <Tr 
                    key={client.id}
                    // Session log view disabled - uncomment below to re-enable
                    // onClick={(e) => {
                    //   // Prevent row click when clicking action buttons
                    //   if (e.target.closest('button')) return;
                    //   setSelectedClientForLog(client);
                    // }}
                    // cursor="pointer"
                    // _hover={{ bg: 'gray.50' }}
                  >
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{formatNameLastCommaFirst(client.name)}</Text>
                        {client.nickname && (
                          <Text fontSize="sm" color="gray.600">"{client.nickname}"</Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          {client.pwd ? 'PWD' : 'Non-PWD'}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Text>{getClientDisplayAge(client) ?? 'N/A'}/{client.gender?.[0] || 'N/A'}</Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={2}>
                        {client.services
                          .filter(service => {
                            const currentDate = new Date().toISOString().split('T')[0];
                            return (!service.pricing_valid_from || service.pricing_valid_from <= currentDate) &&
                                   (!service.pricing_valid_until || service.pricing_valid_until >= currentDate);
                          })
                          .map((service, index) => (
                            <Box key={index}>
                              <Text fontSize="sm" fontWeight="medium">
                                {service.service_type} - {service.frequency}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {service.schedule}
                              </Text>
                            </Box>
                          ))}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={client.status === 'Active' ? 'green' : 'red'}
                      >
                        {client.status}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<ViewIcon />}
                          onClick={() => handleViewClient(client)}
                          aria-label="View client details"
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">
                {searchTerm || serviceFilter 
                  ? 'No clients found matching your search criteria.' 
                  : 'No clients assigned to you yet.'
                }
              </Text>
            </Box>
          )}
        </VStack>
      )}
  
      {/* Client Detail Modal */}
      <ClientDetailModal
        isOpen={isOpen}
        onClose={onClose}
        client={selectedClient}
      />
    </Box>
  );
};

export default TherapistClientList;